import { hasEncryptedSSO } from 'Constants/Auth';
import { checkSession } from 'Shared/Auth/CheckSession';
import { b64EncodeUnicode } from 'Utils/crypto/b64EncodeUnicode';
import ynToBool from 'Utils/ynToBool';
import axios from 'axios';

const ltmApiUrl = process.env.REACT_APP_LTM_API;
export const premmiarApiUrl = process.env.REACT_APP_PREMMIAR_API;
export const conciergeApiUrl = process.env.REACT_APP_REDEMPTION_CONCIERGE_API_ENDPOINT;

const cloudloyaltyUrl = process.env.REACT_APP_CLOUD_LOYALTY_ENDPOINT;
const CmsStaticURL = process.env.CMS_ENDPOINT;
const subscriptionKey = process.env.REACT_APP_CLOUD_LOYALTY_SUBSCRIPTION;
const refererApi = process.env.REACT_APP_ENVIRONMENT;
const useCacheParameters = process.env.REACT_APP_APIMANAGEMENT_CACHE_AVAILABILITY;
const tagApiUrl = process.env.REACT_APP_PREMMIAR_TAG_API_ENDPOINT;
const redemptionApiUrl = process.env.REACT_APP_PREMMIAR_REDEMPTION_API_ENDPOINT;
const vertemManagerUrl = process.env.REACT_APP_VERTEM_MANAGER;
const showcaseApiUrl = process.env.REACT_APP_SHOWCASE_API_ENDPOINT;
const ltmTrackingUrl = process.env.REACT_APP_TRACKING_ENDPOINT;
export const genericSupplierApiUrl = process.env.REACT_APP_PREMMIAR_GENERIC_SUPPLIER_ENDPOINT;
export const genericSupplierTrackingApiUrl =
  process.env.REACT_APP_TRACKING_PGS_ENDPOINT ??
  process.env.REACT_APP_PREMMIAR_GENERIC_SUPPLIER_ENDPOINT;
export const giftApiUrl = process.env.REACT_APP_GIFT_API_ENDPOINT;
export const vertemGiftApiUrl = process.env.REACT_APP_VERTEM_GIFT_API_ENDPOINT;
const stateInscriptionApiUrl = process.env.REACT_APP_PREMMIAR_STATE_INSCRIPTION_API_ENDPOINT;
const marketplaceReportsUrl = process.env.REACT_APP_PREMMIAR_MARKETPLACE_REPORTS_ENDPOINT;

const hasLikePlusCardApiUrl = process.env.REACT_APP_HAS_LIKE_PLUS_CARD;

const simulation =
  ynToBool(process.env.REACT_APP_HAS_HIERARCHY) && localStorage.getItem('Premmiar@simulation')
    ? JSON.parse(localStorage.getItem('Premmiar@simulation'))
    : null;

export const cloudloyaltyURLOnly = (User, header = {}) => {
  const headers = {
    'Ocp-Apim-Subscription-Key': subscriptionKey,
    'Referer-Api': refererApi,
    Authorization: User.access_token,
  };

  if (useCacheParameters) {
    headers['x-tenant'] = User.profile['custom:campaign_id'];
    headers['x-cache-key'] = window.btoa(User.profile['custom:catalog_id']);
    headers['x-gatewaycache'] = 'true';
  }

  if (header?.milleageVendor) {
    headers['X-Mileage-VendorId'.toLowerCase()] = header?.milleageVendor;
  }

  const clientBase = axios.create({
    baseURL: `${cloudloyaltyUrl}/`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const cloudloyalty = (User) => {
  const headers = {
    'Ocp-Apim-Subscription-Key': subscriptionKey,
    'Referer-Api': refererApi,
    Authorization: User.access_token,
  };

  if (useCacheParameters) {
    headers['x-tenant'] = User.profile['custom:campaign_id'];
    headers['x-cache-key'] = b64EncodeUnicode(User.profile['custom:catalog_id']);
    headers['x-gatewaycache'] = 'true';
  }

  const clientBase = axios.create({
    baseURL: `${cloudloyaltyUrl}/cloudloyalty/`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  clientBase.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error.response.status === 401 && hasEncryptedSSO) {
        checkSession(User)
          .then(({ status }) => {
            if (!status) {
              window.location.href = '/auth/logout';
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }

      return Promise.reject(error);
    },
  );

  return clientBase;
};

export const premmiar = (User) => {
  const headers = {
    Authorization: User.access_token,
    username: User.profile.username || User.profile['custom:login'],
    participantId: User.profile['custom:participant_id'],
    catalogid: process.env.REACT_APP_CATALOG_ID,
    campaignid: process.env.REACT_APP_TENANT_ID,
  };

  const clientBase = axios.create({
    baseURL: process.env.REACT_APP_RATING_API,
    responseType: 'json',
    headers,
  });

  return clientBase;
};

export const CMSEndpoint = () => {
  const clientBase = axios.create({
    baseURL: `${CmsStaticURL}catalogs`,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const VertemManagerEndpoint = (User) => {
  const clientBase = axios.create({
    baseURL: `${vertemManagerUrl}`,
  });

  return clientBase;
};

export const tagEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${tagApiUrl}/api/tag/product/sku/`,
    headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const redemptionEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    username: User.profile.username || User.profile['custom:login'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${redemptionApiUrl}Redemption/`,
    headers: headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const apiRedemption = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    username: User.profile.username || User.profile['custom:login'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${redemptionApiUrl}`,
    headers: headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const syngentaRedemptionEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    username: User.profile.username || User.profile['custom:login'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${redemptionApiUrl}Syngenta/`,
    headers: headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const marketplaceReportsEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    username: User.profile.username || User.profile['custom:login'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${marketplaceReportsUrl}`,
    headers: headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const showcaseEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${showcaseApiUrl}/`,
    headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const trackingLTM = () => {
  const clientBase = axios.create({
    baseURL: `${ltmTrackingUrl}`,
  });

  return clientBase;
};

export const trackingPGS = (User) => {
  const headers = {
    Authorization: `Bearer ${User.access_token}`,
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId:
      simulation && simulation.enabled && simulation.participantId
        ? simulation.participantId
        : User.profile['custom:participant_id'],
  };

  const clientBase = axios.create({
    baseURL: `${genericSupplierTrackingApiUrl}`,
    responseType: 'json',
    headers,
  });

  return clientBase;
};

export const genericSupplierEndpoint = (User) => {
  const headers = {
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId:
      simulation && simulation.enabled && simulation.participantId
        ? simulation.participantId
        : User.profile['custom:participant_id'],
    authorization: `Bearer ${User.access_token}`,
  };

  const clientBase = axios.create({
    baseURL: `${genericSupplierApiUrl}/`,
    headers,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const stateInscriptionEndpoint = (User) => {
  const clientBase = axios.create({
    baseURL: `${stateInscriptionApiUrl}`,
    responseType: 'json',
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const PremmiarEndpoint = (User) => {
  const headers = {
    Authorization: `Bearer ${User.access_token}`,
    campaignId: User.profile['custom:campaign_id'],
    catalogId: User.profile['custom:catalog_id'],
    participantId: User.profile['custom:participant_id'],
    login: User.profile["username"] || User.profile['custom:login'],
  };

  const clientBase = axios.create({
    baseURL: `${premmiarApiUrl}`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const LtmEndpoint = (User) => {
  const headers = {
    'Ocp-Apim-Subscription-Key': subscriptionKey,
    'Referer-Api': refererApi,
    Authorization: User.access_token,
  };

  if (useCacheParameters) {
    headers['x-tenant'] = User.profile['custom:campaign_id'];
    headers['x-cache-key'] = b64EncodeUnicode(User.profile['custom:catalog_id']);
    headers['x-gatewaycache'] = 'true';
  }

  const clientBase = axios.create({
    baseURL: `${ltmApiUrl}`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const mileage = (User, currentSupplierId = null) => {
  const headers = {
    'Ocp-Apim-Subscription-Key': subscriptionKey,
    'Referer-Api': refererApi,
    Authorization: User.access_token,
  };

  if (useCacheParameters) {
    headers['x-tenant'] = User.profile['custom:campaign_id'];
    headers['x-cache-key'] = b64EncodeUnicode(User.profile['custom:catalog_id']);
    headers['x-gatewaycache'] = 'true';
  }
  if (currentSupplierId) {
    headers['X-Mileage-VendorId'] = currentSupplierId;
  }

  const clientBase = axios.create({
    baseURL: `${cloudloyaltyUrl}/mileage-api/`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};

export const ConciergeEndpoint = (headers) => {
  const clientBase = axios.create({
    baseURL: `${conciergeApiUrl}`,
    responseType: 'json',
    headers,
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
  });

  return clientBase;
};
